import styles from "./index.module.scss";
import { NextPage } from "next";
import StockDividendTab from "@/components/dividends-v2/stock/StockDividendTab";
import { Stock } from "@/models/stock";
import Head from "next/head";
import { buildStockOgImage } from "@/lib/stock-og";
import { stockTradingItemIdState } from "@/lib/store";
import { useSetAtom } from "jotai";
import { useEffect } from "react";
import { stockPageGetServerSideProps } from "@/lib/stock-page-ssr";
import { nextDividendLine, stockDescriptions, stockTitle } from "@/lib/stock-seo";
import { stockCanonicalUrl } from "@/lib/stock-url";

/**
 * The stock Dividend tab: the twelve-column box grid of `StockDividendTab` (epic ortex&131), fed by
 * TED `dividends/stock/<tradingitem_id>`. Replaces the legacy `StockYield` page. ETFs keep the tab
 * (StockLinks allow-list) and get only the boxes their payload has data for.
 */
const StockDividendPage: NextPage<{ stock: Stock }> = ({ stock }) => {
    const setStockTradingItemId = useSetAtom(stockTradingItemIdState);
    const nextDividend = stock ? nextDividendLine(stock.next_corporate_action) : "";

    useEffect(() => {
        stock ? setStockTradingItemId(stock.tradingitem_id) : setStockTradingItemId(null);
    }, [stock]);
    return (
        <>
            {stock &&
                (() => {
                    const { ogImageUrl, cardTitle } = buildStockOgImage(stock, "Dividend");
                    return (
                        <Head>
                            <title>{stockTitle(stock, "dividend")}</title>
                            <link rel="canonical" href={stockCanonicalUrl(stock, "dividend")} />
                            <meta name="keywords" content={`${stock.name}, ${stock.exchangesymbol}:${stock.ticker}, Dividend, Yield`} />
                            <meta name="description" content={stockDescriptions.dividend(stock)} />
                            <meta name="twitter:card" content="summary_large_image" />
                            <meta name="twitter:title" content={stockTitle(stock, "dividend")} />
                            <meta name="twitter:description" content={stockDescriptions.dividend(stock)} />
                            <meta name="twitter:url" content={stockCanonicalUrl(stock, "dividend")} />
                            <meta name="twitter:image" content={ogImageUrl} />
                            <meta name="twitter:image:alt" content={cardTitle} />
                            <meta property="og:title" content={stockTitle(stock, "dividend")} />
                            <meta property="og:description" content={stockDescriptions.dividend(stock)} />
                            <meta property="og:url" content={stockCanonicalUrl(stock, "dividend")} />
                            <meta property="og:image" content={ogImageUrl} key="og:image" />
                            <meta property="og:image:width" content="1200" />
                            <meta property="og:image:height" content="630" />
                            <meta property="og:image:alt" content={cardTitle} />
                        </Head>
                    );
                })()}
            <div className={styles.StockDividendPage}>
                {stock ? (
                    <>
                        {/* From the stock payload, so in the server's HTML; nothing when the next action is not a dividend. */}
                        {nextDividend && <p className={styles.nextDividend}>{nextDividend}</p>}
                        <StockDividendTab stock={stock} />
                    </>
                ) : (
                    <h1>Stock not found.</h1>
                )}
            </div>
        </>
    );
};

export const getServerSideProps = stockPageGetServerSideProps({ tab: "dividend" });

export default StockDividendPage;
